<template>
  <div class="w-full max-w-full flex p-2">
    <div class="flex-grow flex items-center flex-wrap gap-2">
      <div v-if="loading" class="skeleton w-44 h-8 rounded-md" />
      <button v-else-if="reportType === 'comparative'" class="base-btn flex items-center gap-1.5 p-1.5 pl-2.5 fade-in" @click="toggleGroupBuilder">
        <BaseText class="text-text-normal" size="sm">
          Comparing
        </BaseText>
        <BaseText class="text-text-muted" type="label" size="sm">
          {{ totalGroups }} groups
        </BaseText>
        <MaximizeIcon class="text-icon-normal fade-in" v-if="!isGroupBuilderOpen"/>
        <MinimizeIcon class="text-icon-normal fade-in" v-else/>
      </button>
      <BaseSingleDropdown
        v-else
        class="fade-in"
        :selected-obj="selectedGroupBy"
        :options="groupByOptions"
        :min-width="'128px'"
        :selected-label="(opt) => `${opt.name}`"
        :option-label="(opt) => opt.name"
        selection-prefix="Group By"
        placeholder="Select a grouping"
        @change="(selected) => $emit('update:groupBy', {type: 'group_by', value: selected.id})"
      >
      <template #icon>
        <DotCircleIcon/>
      </template>
      </BaseSingleDropdown>
      <!-- Add Filter Button -->
      <div v-on-clickaway="() => {closeFilterCreation()}" class="relative">
        <button class="flex items-center p-1 rounded-md transition-colors duration-100"
        :class="isBuildingFilter ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
        @click="toggleFilterCreation">
          <FilterIcon class="h-4" />
          <BaseText type="label" size="sm" class="text-text-muted pl-1 py-0.5 pr-2">
            Add Filter
          </BaseText>
        </button>
        <!-- Filter Type Selector Dropdown -->
        <transition>
          <div v-if="showFilterSelector" class="filter-selector-dropdown">
            <PropertySelectDropdown
              search-placeholder="Search Filters"
              @optionSelected="handleFilterOptionSelected"
              @close="closeFilterCreation"
            />
          </div>
        </transition>
        <!-- Filter Query Builder -->
        <transition>
          <div v-if="builderInitFilterOption" class="filter-query-builder">
            <LensFilterQueryBuilder
              :init-filter-option="builderInitFilterOption"
              :currency-region="currentLens?.currency"
              @createFilter="handleFilterCreated"
              @close="closeFilterCreation"
            />
          </div>
        </transition>
      </div>
      <!-- Applied Filters List -->
      <div v-for="(filter, index) in appliedFilters" :key="`applied-filter-${index}`" 
      class="relative" :ref="`applied-filter-${index}`"
      v-on-clickaway="() => { if (editingFilterIndex === index) editingFilterIndex = null }">
        <AppliedFilter 
          :filterConditions="filter"
          :isEditing="editingFilterIndex === index"
          :currency-region="currentLens?.currency"
          @selected="toggleEditAppliedFilter(index)"
          @remove="handleFilterRemoved(index)"
        />
        <!-- Edit Filter Query Builder -->
        <transition>
          <div v-if="editingFilterIndex === index" class="edit-filter-query-builder"
          :class="getEditFilterDropdownPositionClasses(index)">
            <LensFilterQueryBuilder 
              :initFilterConditions="filter"
              :currency-region="currentLens?.currency"
              @createFilter="handleEditAppliedFilter($event, index)"
              @close="editingFilterIndex = null"
            />
          </div>
        </transition>
      </div>
    </div>
    <!-- Date Range Selector -->
    <div class="flex items-start flex-shrink-0">
      <div class="relative" v-on-clickaway="() => { showDateRangeSelector = false }">
        <button class="flex items-center gap-1.5 pl-2 py-1.5 pr-2.5 rounded-md flex-shrink-0"
        :class="showDateRangeSelector ? 'bg-neutral-25' : 'bg-transparent hover:bg-neutral-25'"
        @click="showDateRangeSelector = !showDateRangeSelector">
          <CalendarIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ dateFilterLabel }}
          </BaseText>
        </button>
        <transition>
          <div v-if="showDateRangeSelector" class="date-selector-dropdown">
            <DateRangeSelectorDropdown 
              :selectedDateRange="selectedDateRange"
              :isLensReport="true"
              @update:selectedDateRange="$emit('update:selectedDateRange', $event)"
              @close="showDateRangeSelector = false"
            />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import { getMatchingPresetName, formatCustomDateRangeLabel } from '../../../utils/dateUtils'
import PropertySelectDropdown from './PropertySelectDropdown.vue'
import LensFilterQueryBuilder from './LensFilterQueryBuilder.vue'
import AppliedFilter from './AppliedFilter.vue'
import DateRangeSelectorDropdown from '../../search/TopBarComponents/DateRangeSelectorDropdown.vue'

import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import CalendarIcon from '../../globals/Icons/CalendarIcon.vue'
import BaseSingleDropdown from '../../globals/BaseSingleDropdown.vue'
import FilterIcon from '../../globals/Icons/FilterIcons/FilterIcon.vue'
import MinimizeIcon from '../../globals/Icons/MinimizeIcon.vue'
import MaximizeIcon from "../../globals/Icons/MaximizeIcon.vue"
import DotCircleIcon from "../../globals/Icons/DotCircleIcon.vue"
export default {
  name: 'LensFilterToolbar',
  mixins: [clickaway],
  components: {
    PropertySelectDropdown,
    LensFilterQueryBuilder,
    AppliedFilter,
    DateRangeSelectorDropdown,
    FilterIcon,
    ChevronIcon,
    CalendarIcon,
    MinimizeIcon,
    MaximizeIcon,
    DotCircleIcon,
    BaseSingleDropdown
  },
  props: {
    currentLens: {
      type: Object,
      default: () => {}
    },
    appliedFilters: {
      type: Array,
      required: true
    },
    groupBy: {
      type: String,
      default: () => 'ad_name'
    },
    selectedDateRange: {
      type: Object,
      default: () => {}
    },
    reportType: {
      type: String,
      default: () => ''
    },
    totalGroups: {
      type: Number,
      default: 0
    },
    isGroupBuilderOpen: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const groupByOptions = [
      { id: 'ad_name', name: 'Ad Name' },
      { id: 'ad_copy', name: 'Copy' },
      { id: 'creative', name: 'Creative' },
      { id: 'destination_url', name: 'Landing Page' },
      { id: 'ad_title', name: 'Headline' }
    ]
    return {
      builderInitFilterOption: null,
      groupByOptions,
      dateFilterLabel: 'All Time',
      showFilterSelector: false,
      showDateRangeSelector: false,
      editingFilterIndex: null
    }
  },
  watch: {
    selectedDateRange (newVal) {
      if (!newVal?.start || !newVal?.end) {
        this.dateFilterLabel = 'All Time'
      } else if (newVal.isRelativeToToday) {
        this.dateFilterLabel = getMatchingPresetName(newVal) || formatCustomDateRangeLabel(newVal.start, newVal.end)
      } else {
        this.dateFilterLabel = formatCustomDateRangeLabel(newVal.start, newVal.end)
      }
    }
  },
  computed: {
    selectedGroupBy () {
      return this.groupByOptions.find((opt) => opt.id === this.groupBy)
    },
    isBuildingFilter () {
      return this.showFilterSelector || this.builderInitFilterOption
    }
  },
  methods: {
    toggleGroupBuilder () {
      const parentScrollContainer = this.$parent.$el
      if (parentScrollContainer && parentScrollContainer.scrollTop > 100) {
        parentScrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
        this.$emit('toggleComparativeBuilder', true)
      } else {
        this.$emit('toggleComparativeBuilder')
      }
    },
    handleFilterCreated (filterConditions) {
      const newFilters = [...this.appliedFilters, filterConditions]
      this.$emit('update:appliedFilters', newFilters)
    },
    handleFilterRemoved (index) {
      const newFilters = this.appliedFilters.filter((_, i) => i !== index)
      this.$emit('update:appliedFilters', newFilters)
    },
    handleFilterOptionSelected (option) {
      this.builderInitFilterOption = option
      this.showFilterSelector = false
    },
    handleEditAppliedFilter (filterConditions, index) {
      const newFilters = [...this.appliedFilters]
      newFilters[index] = filterConditions
      this.$emit('update:appliedFilters', newFilters)
    },
    toggleFilterCreation () {
      console.log('hi', this.isBuildingFilter)
      if (this.isBuildingFilter) {
        this.closeFilterCreation()
      } else {
        this.showFilterSelector = true
      }
    },
    closeFilterCreation () {
      console.log('closing filter')
      this.showFilterSelector = false
      this.builderInitFilterOption = null
    },
    toggleEditAppliedFilter (index) {
      if (this.editingFilterIndex !== index) {
        this.editingFilterIndex = index
      } else {
        this.editingFilterIndex = null
      }
    },
    getEditFilterDropdownPositionClasses (index) {
      // Determines whether to left-align, center, or right-align the edit filter dropdown
      const filterElement = this.$refs[`applied-filter-${index}`][0]
      const mainLayoutParent = document.querySelector('.main-layout')
      if (!filterElement || !mainLayoutParent) return 'left-0'

      const mainLayoutRect = mainLayoutParent.getBoundingClientRect()
      const filterElmRect = filterElement.getBoundingClientRect()
      const filterElmPos = (filterElmRect.left - mainLayoutRect.left) + (filterElmRect.width / 2)
      
      const sectorWidth = mainLayoutRect.width / 3
      const sectorBoundaries = [sectorWidth, sectorWidth * 2]

      if (filterElmPos < sectorBoundaries[0]) {
        return 'left-0'
      } else if (filterElmPos < sectorBoundaries[1]) {
        return 'left-1/2 transform -translate-x-1/2'
      } else {
        return 'right-0'
      }
    }
  }
}
</script>

<style scoped>
.group-by-selector {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.filter-selector-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
}
.filter-query-builder {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
}
.edit-filter-query-builder {
  position: absolute;
  top: calc(100% + 4px);
}
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 8px 6px 8px;
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0.25 }
  to { opacity: 1 }
}

</style>
